/**
 * An agent whose app is its own deployed service, shown in the pane.
 *
 * Joël, 7 October 2026: "UI is also a service". An agent's screens leave this
 * bundle for their own repository and host (the Studio: tricorder-studio-web at
 * alg.<env>). The main app cannot import them any more, so it frames the app at
 * its own origin, with `?embed`: the app leaves out its own way back to the
 * twin, and talks to this host only through the two messages of
 * embedContract.ts.
 *
 * Same site, so the domain-wide refresh cookie signs the frame in; the app's
 * hosting sends `Content-Security-Policy: frame-ancestors` naming this origin.
 *
 * THE INNER PATH IS THE URL, as for a bundled agent (AgentFrame):
 * `/home/a/<slug>/<the app's path>`. The frame starts at the path the URL names
 * when it mounts, and every `navigated` message replaces the URL, so reload,
 * Back and a shared link reopen the same screen. The frame's src is NOT kept in
 * step with the URL afterwards: the app moves itself, and re-pointing the src
 * would reload it on every one of its own steps.
 */
import { useEffect, useRef, useState, type ComponentType } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { agentFrameBasePath, agentPathFromFramePath } from '@/lib/routes'
import { agentFullScreenUrl } from '@/lib/sessionRouting'
import type { AgentFaceProps } from './agentFace'
import { EMBED_PARAM, isEmbedMessage } from './embedContract'

function frameSrc(slug: string, path: string): string | null {
  const url = agentFullScreenUrl(slug, path)
  if (!url) return null
  const u = new URL(url)
  u.searchParams.set(EMBED_PARAM, '')
  return u.toString()
}

function RemoteFrame({ slug, session }: { slug: string; session?: AgentFaceProps['session'] }) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const frameRef = useRef<HTMLIFrameElement>(null)

  // Where the frame starts: the path the URL names, else a session's own entry
  // (the app decides which screen a session opens on), else its home.
  const [src] = useState(() => {
    const fromUrl = agentPathFromFramePath(slug, pathname)
    const start = fromUrl !== '/' ? fromUrl : session ? `/sessions/${session.session.id}` : '/'
    return frameSrc(slug, start)
  })
  const origin = src ? new URL(src).origin : null

  useEffect(() => {
    if (!origin) return
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== origin || event.source !== frameRef.current?.contentWindow) return
      if (!isEmbedMessage(event.data)) return
      if (event.data.type === 'navigated') {
        const base = agentFrameBasePath(slug)
        const next = event.data.path === '/' ? base : `${base}${event.data.path}`
        // Replace: the frame's own steps are already in the tab's history.
        if (next !== window.location.pathname + window.location.search) navigate(next, { replace: true })
        return
      }
      // signed-out: the frame found no session, so neither does this tab.
      const returnTo = encodeURIComponent(window.location.href)
      window.location.assign(`/login?returnTo=${returnTo}`)
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [origin, slug, navigate])

  if (!src) return null
  return (
    <iframe
      ref={frameRef}
      src={src}
      title={slug}
      className="h-full w-full border-0"
      data-testid="remote-agent-frame"
      // The app's own host; the browser sends no referrer path beyond the origin.
      referrerPolicy="strict-origin-when-cross-origin"
    />
  )
}

const REMOTE_FACES = new Map<string, ComponentType<AgentFaceProps>>()

/**
 * The face for an agent with its own host. One component per slug, created
 * once, so the registry hands React the same component on every render and the
 * frame is never remounted by a re-render.
 */
export function remoteAgentFace(slug: string): ComponentType<AgentFaceProps> {
  let face = REMOTE_FACES.get(slug)
  if (!face) {
    face = function RemoteAgentFace({ session }: AgentFaceProps) {
      // Keyed on the session: another conversation of the same agent starts the
      // frame again at that session's screen.
      return <RemoteFrame key={session?.session.id ?? 'face'} slug={slug} session={session} />
    }
    REMOTE_FACES.set(slug, face)
  }
  return face
}

